<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useModalConfirmationStore } from '@/stores/modal/confirmation'
import { createBetaLayoutPreview } from '@/services/beta-dashboard'
import type { BetaLayoutPreview, BetaTemplate } from '@/types/beta-dashboard'

const props = defineProps<{
    template: BetaTemplate
    siteId: number
    applying: boolean
    applyError: string | null
}>()
const emit = defineEmits<{
    close: []
    apply: [token: string]
    'reset-error': []
}>()
const confirmation = useModalConfirmationStore()
const panel = ref<HTMLElement | null>(null)
const preview = ref<BetaLayoutPreview | null>(null)
const iframe = ref<HTMLIFrameElement | null>(null)
const device = ref<'desktop' | 'mobile'>('desktop')
const state = ref<'creating' | 'loading' | 'ready' | 'error' | 'expired'>('creating')
const error = ref('')
const frameKey = ref(0)
const origin = computed(() => preview.value ? new URL(preview.value.previewUrl).origin : '')
let previousFocus: HTMLElement | null = null
let previousOverflow = ''
let generation = 0
let creationController: AbortController | undefined
let loadingTimer: ReturnType<typeof setTimeout> | undefined
let expirationTimer: ReturnType<typeof setTimeout> | undefined

const unavailable = (message = '', expired = false) => {
    clearTimeout(loadingTimer)
    clearTimeout(expirationTimer)
    preview.value = null
    state.value = expired ? 'expired' : 'error'
    error.value = message
}

const startLoading = () => {
    clearTimeout(loadingTimer)
    state.value = 'loading'
    loadingTimer = setTimeout(() => {
        unavailable('O site não confirmou o carregamento. Verifique a conexão e tente novamente.')
    }, 30000)
}

const generate = async () => {
    const request = ++generation
    creationController?.abort()
    const controller = creationController = new AbortController()
    clearTimeout(loadingTimer)
    clearTimeout(expirationTimer)
    emit('reset-error')
    preview.value = null
    state.value = 'creating'
    error.value = ''
    loadingTimer = setTimeout(() => {
        generation += 1
        controller.abort()
        unavailable('A criação da prévia demorou demais. Verifique a conexão e tente novamente.')
    }, 30000)
    try {
        const result = await createBetaLayoutPreview(props.siteId, props.template.id, controller.signal)
        if (request !== generation) return
        const remaining = Date.parse(result.expiresAt) - Date.now()
        if (remaining <= 0) throw new Error('A prévia expirou. Gere uma nova prévia.')
        preview.value = result
        startLoading()
        expirationTimer = setTimeout(() => unavailable('', true), remaining)
    } catch (cause) {
        if (request !== generation) return
        unavailable(cause instanceof Error ? cause.message : 'Não foi possível gerar a prévia.')
    }
}

const onMessage = (event: MessageEvent) => {
    if (!preview.value || event.source !== iframe.value?.contentWindow || event.origin !== origin.value) return
    if (!event.data || event.data.token !== preview.value.token || state.value === 'expired' || props.applyError) return
    switch (event.data.type) {
        case 'uli:layout-preview:ready': {
            if (event.data.url !== preview.value.previewUrl) return
            clearTimeout(loadingTimer)
            state.value = 'ready'
            break
        }
        case 'uli:layout-preview:expired':
            unavailable('', true)
            break
        case 'uli:layout-preview:stale':
        case 'uli:layout-preview:error':
            unavailable(event.data.type === 'uli:layout-preview:stale'
                ? 'O site ou o modelo mudou. Gere uma nova prévia antes de aplicar.'
                : 'Não foi possível renderizar o layout. Gere uma nova prévia.')
    }
}

const handshake = () => {
    if (!preview.value || !origin.value) return
    iframe.value?.contentWindow?.postMessage({
        type: 'uli:layout-preview:init', token: preview.value.token,
    }, origin.value)
}

const reload = () => {
    if (!preview.value || props.applying) return
    startLoading()
    frameKey.value += 1
}
const close = () => { if (!props.applying && !confirmation.isOpenModal) emit('close') }
const onKeydown = (event: KeyboardEvent) => {
    if (confirmation.isOpenModal) return
    if (event.key === 'Escape') { event.preventDefault(); close(); return }
    if (event.key !== 'Tab') return
    const controls = Array.from(panel.value?.querySelectorAll<HTMLElement>('button:not(:disabled), iframe') ?? [])
    const first = controls[0]
    const last = controls[controls.length - 1]
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.value)) {
        event.preventDefault(); last?.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first?.focus()
    }
}
watch(() => confirmation.isOpenModal, async (open) => {
    if (!open) { await nextTick(); panel.value?.focus() }
})
watch(() => props.applyError, (message) => {
    if (message) unavailable(message)
})
onMounted(() => {
    previousFocus = document.activeElement as HTMLElement | null
    previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    panel.value?.focus()
    document.addEventListener('keydown', onKeydown)
    window.addEventListener('message', onMessage)
    generate()
})
onBeforeUnmount(() => {
    document.body.style.overflow = previousOverflow
    previousFocus?.focus()
    document.removeEventListener('keydown', onKeydown)
    generation += 1
    creationController?.abort()
    clearTimeout(loadingTimer)
    clearTimeout(expirationTimer)
    window.removeEventListener('message', onMessage)
})
</script>

<template>
    <Teleport to="body">
    <div class="beta-layout-preview">
        <div class="beta-layout-preview__backdrop" aria-hidden="true" />
        <div class="beta-layout-preview__position">
            <section ref="panel" class="beta-layout-preview__panel" role="dialog" aria-modal="true" aria-labelledby="layout-preview-title" tabindex="-1">
                <header>
                    <div>
                        <h2 id="layout-preview-title">Prévia · {{ props.template.description }}</h2>
                        <p>Seu site continua publicado com o layout atual.</p>
                    </div>
                    <button type="button" aria-label="Fechar prévia" :disabled="props.applying" @click="close">✕</button>
                </header>
                <div class="beta-layout-preview__toolbar">
                    <div role="group" aria-label="Dispositivo da prévia">
                        <button type="button" :aria-pressed="device === 'desktop'" @click="device = 'desktop'">Desktop</button>
                        <button type="button" :aria-pressed="device === 'mobile'" @click="device = 'mobile'">Mobile</button>
                    </div>
                    <button type="button" :disabled="state !== 'ready' || props.applying" @click="reload">Recarregar</button>
                </div>
                <div class="beta-layout-preview__stage" :aria-busy="state === 'creating' || state === 'loading'">
                    <iframe
                        v-if="preview"
                        :key="`preview-${frameKey}`"
                        ref="iframe"
                        :src="preview.previewUrl"
                        :class="{ 'is-mobile': device === 'mobile' }"
                        title="Prévia do seu site com o layout selecionado"
                        sandbox="allow-scripts allow-same-origin"
                        referrerpolicy="no-referrer"
                        @load="handshake"
                    />
                    <div v-show="state !== 'ready'" class="beta-layout-preview__status" role="status">
                        <template v-if="state === 'creating' || state === 'loading'">
                            <span class="beta-layout-preview__spinner" aria-hidden="true" />
                            <strong>{{ state === 'creating' ? 'Preparando seu site…' : 'Carregando prévia…' }}</strong>
                        </template>
                        <template v-else>
                            <strong>{{ state === 'expired' ? 'Esta prévia expirou' : 'Prévia indisponível' }}</strong>
                            <p>{{ state === 'expired' ? 'Gere uma nova versão para visualizar novamente.' : error }}</p>
                            <button type="button" :disabled="props.applying" @click="generate">Gerar nova prévia</button>
                        </template>
                    </div>
                </div>
                <footer>
                    <p>Prévia visual da página inicial. Role para ver o conteúdo; links e interações estão desativados.</p>
                    <button
                        type="button"
                        class="beta-layout-preview__apply"
                        :disabled="state !== 'ready' || props.applying || Boolean(props.applyError)"
                        @click="preview && emit('apply', preview.token)"
                    >{{ props.applying ? 'Aplicando…' : 'Aplicar layout' }}</button>
                </footer>
            </section>
        </div>
    </div>
    </Teleport>
</template>

<style scoped>
.beta-layout-preview { position: relative; z-index: 220; }
.beta-layout-preview__backdrop { position: fixed; inset: 0; background: #13243bc4; }
.beta-layout-preview__position { position: fixed; inset: 0; display: flex; padding: 24px; align-items: center; justify-content: center; }
.beta-layout-preview__panel { display: flex; flex-direction: column; width: min(1440px, 100%); height: calc(100dvh - 48px); overflow: hidden; border-radius: 20px; background: #fff; color: #172d4a; box-shadow: 0 25px 90px #0003; }
header, footer { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 18px 24px; }
header h2 { font-size: 18px; font-weight: 800; }
header p, footer p { margin-top: 4px; font-size: 12px; color: #63758b; }
header button { padding: 8px 12px; border-radius: 10px; background: #f3f5f9; }
.beta-layout-preview__toolbar { display: flex; align-items: center; justify-content: space-between; padding: 10px 24px; border-top: 1px solid #edf1f6; border-bottom: 1px solid #edf1f6; font-size: 12px; }
.beta-layout-preview__toolbar button { padding: 8px 14px; border-radius: 9px; }
.beta-layout-preview__toolbar [aria-pressed="true"] { color: var(--main-color, #7036f5); background: #f0eaff; font-weight: 750; }
.beta-layout-preview__stage { position: relative; display: flex; justify-content: center; flex: 1; min-height: 0; background: #e9eef5; }
iframe { width: 100%; height: 100%; border: 0; background: #fff; }
iframe.is-mobile { max-width: 390px; }
.beta-layout-preview__status { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; padding: 24px; background: #f7f9fcef; text-align: center; }
.beta-layout-preview__status p { max-width: 460px; font-size: 14px; color: #63758b; }
.beta-layout-preview__status button, .beta-layout-preview__apply { flex: none; padding: 12px 20px; border-radius: 12px; background: var(--main-color, #7036f5); color: white; font-size: 13px; font-weight: 750; }
button:disabled { opacity: .5; cursor: not-allowed; }
button:focus-visible { outline: 3px solid #9c74f8; outline-offset: 3px; }
.beta-layout-preview__spinner { width: 26px; height: 26px; border: 3px solid #e4d9ff; border-top-color: #7036f5; border-radius: 50%; animation: preview-spin 1s linear infinite; }
@keyframes preview-spin { to { transform: rotate(360deg); } }
@media (max-width: 640px) {
    .beta-layout-preview__position { padding: 0; }
    .beta-layout-preview__panel { height: 100dvh; border-radius: 0; }
    header, footer { padding: 14px 16px; gap: 12px; }
    header h2 { font-size: 16px; }
    footer { align-items: stretch; flex-direction: column; }
    footer p { font-size: 11px; margin: 0; }
    .beta-layout-preview__toolbar { padding: 8px 12px; }
}
@media (prefers-reduced-motion: reduce) { .beta-layout-preview__spinner { animation: none; } }
</style>
